import React, { useState, useEffect, useCallback } from 'react';
import { bool, func, object } from 'prop-types';
import { noop } from 'lodash';
import classNames from 'classnames';
import { Icon } from '@fiverr-private/fit';
import { s_play_circle, s_pause_circle } from '@fiverr-private/icons';
import { LazyComponent } from '@fiverr-private/orca';
import { logger } from '@fiverr-private/obs';
import Image from '../../Image';
import { enrichMedia } from '../../utils/enrichMedia';
import { loadVolume, storeVolume, handlePressEvents, validVolume } from './utils';
import AudioLength from './AudioLength';
import VolumeBar from './VolumeBar';
import '../slide.scss';
import './audio.scss';

const lazyImportWaveform = () => import(/* webpackChunkName: 'Waveform' */ './Waveform');

const onLazyImportWaveformError = (error) => {
    logger.error('Failed to lazy load waveform', { error });
};

const AudioSlide = ({
    isActive = false,
    slide,
    onClick = noop,
    expanded = false,
    preload = false,
    reportAction = noop,
}) => {
    const [currentPosition, setCurrentPosition] = useState(0);
    const [totalLength, setTotalLength] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [shouldImportWaveform, setShouldImportWaveform] = useState(false);
    const [volume, setVolume] = useState(loadVolume);

    useEffect(() => {
        if (isActive) {
            setVolume(loadVolume());
        } else {
            setIsPlaying(false);
            setIsLoaded(false);
            setIsLoading(false);
        }
    }, [isActive]);

    useEffect(() => {
        setShouldImportWaveform(true);
    }, []);

    const handleTogglePlay = (e) => {
        e?.preventDefault();
        setIsPlaying(!isPlaying);
        reportAction({
            isPlaying: !isPlaying,
        });
    };

    const handlePosChange = useCallback(({ position }) => {
        setCurrentPosition(position);
    }, []);

    const handlePlayerReady = useCallback(({ duration }) => {
        setTotalLength(duration);
        setIsLoaded(true);
        setIsLoading(false);
        setIsPlaying(true);
    }, []);

    const handleVolumeChange = (newVolume) => {
        if (validVolume(newVolume)) {
            const roundedVolume = parseFloat(newVolume.toFixed(2));
            setVolume(roundedVolume);
            storeVolume(roundedVolume);
        }
    };

    const seek = (offset) => {
        let nextPosition = currentPosition + offset;
        nextPosition = Math.min(Math.max(nextPosition, 0), totalLength);
        setCurrentPosition(nextPosition);
    };

    const handleKeydown = (e) => {
        e?.preventDefault();

        if (isActive) {
            handlePressEvents(e.keyCode, handleTogglePlay, seek, handleVolumeChange, volume);
        }
    };

    const loadAudioPlayer = (e) => {
        e?.preventDefault();
        setIsLoading(true);
        reportAction({
            isPlaying: true,
        });
    };

    const { name, src } = slide;
    const media = enrichMedia(slide);
    const showPlayButton = !isLoading && !isLoaded;
    const thumbClasses = classNames('thumbnail', 'audio', { 'in-progress': isLoading });
    const slideClasses = classNames('slide', 'slide-audio');
    const lazyLoad = !isActive && !preload;
    const previewSrc = expanded ? media.original : media.medium;
    const waveformProps = {
        url: src,
        position: currentPosition,
        onPositionChange: handlePosChange,
        onReady: handlePlayerReady,
        playing: isPlaying,
        volume,
    };

    return (
        <div className={slideClasses} onKeyDown={handleKeydown} onClick={onClick}>
            <div className={thumbClasses} tabIndex="0">
                {showPlayButton && (
                    <Icon size={80} className="slide-play-button" onClick={loadAudioPlayer}>
                        {s_play_circle}
                    </Icon>
                )}

                <Image lazyLoad={lazyLoad} isActive={isActive} alt={name} src={previewSrc} />

                {!showPlayButton && (
                    <div className="audio-player">
                        <div className="top-bar">
                            <AudioLength currentPosition={currentPosition} totalLength={totalLength} />
                            <VolumeBar volume={volume} handleVolumeChange={handleVolumeChange} />
                        </div>
                        <div className="wrap-bottom-bar">
                            <div className="bottom-bar">
                                <Icon size={44} className="play-button" onClick={handleTogglePlay}>
                                    {isPlaying ? s_pause_circle : s_play_circle}
                                </Icon>
                                <div className="waveform">
                                    <LazyComponent
                                        lazyImport={lazyImportWaveform}
                                        shouldImport={shouldImportWaveform}
                                        onError={onLazyImportWaveformError}
                                        componentProps={waveformProps}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

AudioSlide.propTypes = {
    isActive: bool,
    slide: object,
    onClick: func,
    expanded: bool,
    preload: bool,
    reportAction: func,
};

export default AudioSlide;
